// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { Table, message, Button, Divider } from "antd";
import { navigate, RouteComponentProps } from "@reach/router";
import PageTitle from "../../../components/PageTitle";
import PageHeader from "../../../components/PageHeader";
import PageBody from "../../../components/PageBody";
import { useTranslation } from "react-i18next";
import { SpinIcon } from "../../../components/SpinIcon";
import { columns } from "./NewsListColumns";
import {
  requestArchiveNews,
  requestDeleteNews,
  requestDuplicateNews,
  requestGetNewsList,
  requestWorkflowPublishNews,
  requestWorkflowUnpublishNews
} from "../../../../state/news/NewsEffects";
import {
  deleteNews,
  setNews,
  updateNews
} from "../../../../state/news/NewsEvents";
import { useStore } from "effector-react";
import {
  AuthentifiedUser,
  NewsInfoResponseDTO,
  NewsSearchRequestDTO
} from "../../../../api/backend-openapi";
import { NewsStore } from "../../../../state/news/NewsStore";
import { PlusCircleOutlined } from "@ant-design/icons/lib";
import { SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import NewsListSearchForm from "./NewsListSearchForm";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";
import { NboAction } from "../../../types/AuthTypes";
import { orderDescend } from "../../../../utils/Constant";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { displayError } from "../../../../utils/displayError";
import { BookOutlined, SendOutlined, StopOutlined } from "@ant-design/icons";
import { NewsWorkflowStatus } from "../../../types/NewsTypes";
import { showConfirm } from "../../../components/ConfirmModal";
import { ParsedResponse } from "../../../../utils/rest/ServerResponseParse";
import {
  preferedRowsPerPage,
  preferedRowsPerPageChanged
} from "../../../../state/localPreferences/localPreferences";

const NewsListScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();

  const newsStore: NewsInfoResponseDTO[] | null = useStore(NewsStore);

  const branchList = useStore(BranchesStore);

  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );

  const canCreate: boolean =
    authentifiedUser?.rights.nboNews?.includes(NboAction.nboCreate) || false;
  const canDelete: boolean =
    authentifiedUser?.rights.nboNews?.includes(NboAction.nboDelete) || false;

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(true);
  const [searchDto, setSearchDto] = useState<NewsSearchRequestDTO | {}>({});

  const rowsPerPage = useStore(preferedRowsPerPage);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: rowsPerPage,
    showSizeChanger: true
  });

  const [selectedRowList, setSelectedRowList] = useState<NewsInfoResponseDTO[]>(
    []
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const refreshNewList = () => {
    setSelectedRowKeys([]);
    setSelectedRowList([]);
    requestGetNewsList({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      },
      dto: { archived: false }
    });
  };

  // call getNews on page load
  useEffect(() => {
    refreshNewList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return requestGetNewsList.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNews(result.data.listNewsDTO);
        setDataHasLoaded(true);
        setPagination({
          ...pagination,
          total: result.data.count
        });
        preferedRowsPerPageChanged(pagination.pageSize);
      } else {
        displayError("news.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestDeleteNews.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteNews(params.id);
        message.success(t("news.global_buttons.delete.success"));
      } else {
        displayError("news.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestWorkflowPublishNews.done.watch(({ result }) => {
      if (result.ok && result.data) {
        updateNews(result.data);
        message.success(t("news.global_buttons.publish.success"));
      } else {
        displayError("news.global_buttons.publish.error");
      }
    });
  });

  useEffect(() => {
    return requestWorkflowUnpublishNews.done.watch(({ result }) => {
      if (result.ok && result.data) {
        updateNews(result.data);
        message.success(t("news.global_buttons.unpublish.success"));
      } else {
        displayError("news.global_buttons.unpublish.error");
      }
    });
  });

  useEffect(() => {
    return requestDuplicateNews.done.watch(({ result }) => {
      if (result.ok && result.data) {
        navigate(`/news/${result.data.id}`);
        message.success(t("edit_news.duplicate_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  const handleTableChange = async (
    newPagination: any,
    _filters: any,
    sorter: SorterResult<any> | SorterResult<any>[]
  ) => {
    const restSorter = Array.isArray(sorter)
      ? sorter.length > 0
        ? sorter[0]
        : undefined
      : sorter;
    const searchCriteria = {
      pagination: newPagination,
      dto: searchDto,
      sorter: restSorter
    };
    await requestGetNewsList(searchCriteria);
    setPagination(newPagination);
    preferedRowsPerPageChanged(newPagination.pageSize);
  };

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: NewsInfoResponseDTO[]
  ) => {
    setSelectedRowList(selectedRows);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  };

  async function StopProcess(response: ParsedResponse<NewsInfoResponseDTO>) {
    if (
      response.responseCode !== 200 &&
      response.responseCode !== 202 &&
      response.responseCode !== 204
    ) {
      await new Promise((resolve, reject) => {
        showConfirm(
          t("edit_news.modal.error_group_action"),
          () => reject("reject"),
          () => resolve("continue"),
          undefined,
          undefined,
          "Oui",
          "Non"
        );
      });
    }
  }

  const archiveNewsAction = async () => {
    let pause = false;
    for (let i = 0; i < selectedRowList.length && !pause; i++) {
      try {
        const res = await requestArchiveNews({
          id: selectedRowList[i].id
        });
        await StopProcess(res);
      } catch (err) {
        pause = true;
      }
    }
  };

  const publishNewsAction = async () => {
    let pause = false;
    for (let i = 0; i < selectedRowList.length && !pause; i++) {
      try {
        const res = await requestWorkflowPublishNews({
          id: selectedRowList[i].id,
          dto: { isPublish: true }
        });
        await StopProcess(res);
      } catch (err) {
        pause = true;
      }
    }
  };

  const unpublishNewsAction = async () => {
    let pause = false;
    for (let i = 0; i < selectedRowList.length && !pause; i++) {
      try {
        const res = await requestWorkflowUnpublishNews({
          id: selectedRowList[i].id,
          dto: { isPublish: false }
        });
        await StopProcess(res);
      } catch (err) {
        pause = true;
      }
    }
  };

  return (
    <>
      <PageHeader>
        <PageTitle title={t("news_list.title")} />
      </PageHeader>

      <PageBody className="list-body">
        {
          <>
            <NewsListSearchForm
              onSearchDtoUpdate={setSearchDto}
              pagination={pagination}
              dataHasLoaded={dataHasLoaded}
              setDataHasLoaded={setDataHasLoaded}
            />
            <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
              <div className="flex-grow-1 d-flex align-items-center">
                <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
                  <h3 className="text-secondary mb-0">
                    {t("news_list.list_title")}
                  </h3>
                </Divider>
              </div>
              <div className="d-flex flex-row align-items-center justify-content-end flex-wrap mb-3">
                <Button
                  className="btn-success m-2"
                  disabled={
                    !selectedRowList.length ||
                    !!selectedRowList.find(
                      row => row.status !== NewsWorkflowStatus.VALIDATED
                    ) ||
                    !!selectedRowList.find(row => row.archived)
                  }
                  onClick={() =>
                    showConfirm(t("edit_news.modal.publish_selection"), () => {
                      publishNewsAction().then(() => refreshNewList());
                    })
                  }
                >
                  <SendOutlined /> {t("edit_news.publish")}
                </Button>
                <Button
                  className="btn-secondary m-2"
                  disabled={
                    !selectedRowList.length ||
                    !!selectedRowList.find(
                      row => row.status !== NewsWorkflowStatus.PUBLISHED
                    )
                  }
                  onClick={() =>
                    showConfirm(
                      t("edit_news.modal.unpublish_selection"),
                      () => {
                        unpublishNewsAction().then(() => refreshNewList());
                      }
                    )
                  }
                >
                  <StopOutlined /> {t("edit_news.unpublish")}
                </Button>
                <Button
                  className="btn-primary m-2"
                  disabled={
                    !selectedRowList.length ||
                    !!selectedRowList.find(
                      row => row.status === NewsWorkflowStatus.PUBLISHED
                    ) ||
                    !!selectedRowList.find(row => row.archived)
                  }
                  onClick={() => {
                    showConfirm(t("edit_news.modal.archive_selection"), () => {
                      archiveNewsAction().then(() => refreshNewList());
                    });
                  }}
                >
                  <BookOutlined /> {t("edit_news.archive")}
                </Button>
                {canCreate && (
                  <Button
                    type="default"
                    className="btn-secondary m-2"
                    onClick={() => navigate("/news/addNews")}
                  >
                    <PlusCircleOutlined /> {t("news.global_buttons.add")}
                  </Button>
                )}
              </div>
            </div>
            <Table
              style={{ whiteSpace: "break-spaces" }}
              locale={{ emptyText: dataHasLoaded ? "" : " " }}
              loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
              columns={columns(canDelete, branchList)}
              dataSource={newsStore}
              onChange={handleTableChange}
              pagination={pagination}
              showSorterTooltip={false}
              rowKey={record => record.id}
              rowClassName="cursor-pointer"
              onRow={record => {
                return {
                  onClick: () => navigate(`/news/${record.id}`)
                };
              }}
              rowSelection={rowSelection}
            />
          </>
        }
      </PageBody>
    </>
  );
};

export default NewsListScreen;
